import * as GaussianSplats3D from './gaussian-splats-3d.module.js';

export class GaussianSplatsManager {
    static LoadingPhase = {
        DOWNLOADING: 0,
        PROCESSING: 1,
        DONE: 2
    };

    constructor(options = {}) {
        this.viewer = null;
        this.loadingState = {
            downloadProgress: 0,
            processingProgress: 0,
            currentPhase: GaussianSplatsManager.LoadingPhase.DOWNLOADING
        };

        this.options = {
            gpuAcceleratedSort: false,
            dynamicScene: true,
            sharedMemoryForWorkers: false,
            enableOptionalEffects: true,
            downloadWeight: 0.75,
            processingWeight: 0.25,
            ...options
        };

        this.loadedSplatModels = [];
        this.GaussianSplatType = 'GaussianSplat';
        this.modelVisibility = new Map();
    }

    initializeViewer() {
        if (this.viewer) {
            return this.viewer;
        }

        const splatViewerOptions = {
            gpuAcceleratedSort: this.options.gpuAcceleratedSort,
            dynamicScene: this.options.dynamicScene,
            sharedMemoryForWorkers: this.options.sharedMemoryForWorkers,
            enableOptionalEffects: this.options.enableOptionalEffects,
        };

        this.viewer = new GaussianSplats3D.DropInViewer(splatViewerOptions);
        return this.viewer;
    }

    addModel(path, modelId, modelOptions = {}, onProgressCallback = null) {
        if (!this.viewer) {
            this.initializeViewer();
        }

        const modelName = path.split('/').pop();
        this.resetLoadingState();

        const defaultModelOptions = {
            splatAlphaRemovalThreshold: 10,
            position: [0, 0, 0],
            scale: [1, 1, 1],
            rotation: [1, 0, 0, 0],
            showLoadingUI: false,
        };

        const options = {...defaultModelOptions, ...modelOptions};

        options.onProgress = (percentComplete, percentLabel, loaderStatus) => {
            this.handleProgressUpdate(percentComplete, percentLabel, loaderStatus);

            if (onProgressCallback) {
                onProgressCallback(percentComplete, percentLabel, loaderStatus);
            }
        };

        const sceneIndexBeforeLoad = this.getSceneCount();
        const cleanPath = path.replace(/\s*\(\d+\)\s*$/, '');
        const loadModel = this.viewer.addSplatScene(cleanPath, options);

        return loadModel
            .then(() => {
                if (!this.viewer.parent) {
                    wv.allVolumesForFaces.add(this.viewer);
                }

                let loadedSplatModel = this.getScene(sceneIndexBeforeLoad);

                if (!loadedSplatModel) {
                    const currentSceneCount = this.getSceneCount();
                    loadedSplatModel = this.getScene(currentSceneCount - 1);
                }

                if (loadedSplatModel) {
                    loadedSplatModel.modelId = modelId;
                    loadedSplatModel.model_name = modelName;
                    loadedSplatModel.modelType = this.GaussianSplatType;
                    loadedSplatModel.sceneIndex = sceneIndexBeforeLoad;

                    const underlyingViewer = this.viewer.viewer;
                    const splatMesh = underlyingViewer.getSplatMesh();

                    if (splatMesh && splatMesh.scenes && splatMesh.scenes[sceneIndexBeforeLoad]) {
                        const splatMeshScene = splatMesh.scenes[sceneIndexBeforeLoad];
                        splatMeshScene.modelId = modelId;
                        splatMeshScene.model_name = modelName;
                        splatMeshScene.modelType = this.GaussianSplatType;
                        splatMeshScene.sceneIndex = sceneIndexBeforeLoad;
                    }

                    modelManager.setMaterialForModel(modelId, [splatMesh.material]);
                    this.loadedSplatModels.push(loadedSplatModel);
                    this.modelVisibility.set(modelId, true);
                    this.syncModelStateWithViewer();
                }

                return this.viewer;
            })
            .catch(error => {
                if (typeof modelLoadingProgressBar !== 'undefined') {
                    modelLoadingProgressBar.show(false);
                }
                throw error;
            });
    }

    handleProgressUpdate(percentComplete, percentLabel, loaderStatus) {
        if (loaderStatus === GaussianSplatsManager.LoadingPhase.DOWNLOADING) {
            this.loadingState.currentPhase = loaderStatus;
            this.loadingState.downloadProgress = percentComplete;
        } else if (loaderStatus === GaussianSplatsManager.LoadingPhase.PROCESSING) {
            this.loadingState.currentPhase = loaderStatus;
            this.loadingState.processingProgress = percentComplete;
        }

        let overallProgress;
        if (this.loadingState.currentPhase === GaussianSplatsManager.LoadingPhase.DOWNLOADING) {
            overallProgress = percentComplete * this.options.downloadWeight;
        } else {
            overallProgress = (this.options.downloadWeight * 100) +
                (percentComplete * this.options.processingWeight);
        }

        modelLoadingProgressBar.updatePercentage(overallProgress);
    }

    resetLoadingState() {
        this.loadingState = {
            downloadProgress: 0,
            processingProgress: 0,
            currentPhase: GaussianSplatsManager.LoadingPhase.DOWNLOADING
        };
    }

    getScene(indexOrId) {
        if (!this.viewer) return null;
        return this.viewer.getSplatScene(indexOrId);
    }

    getSceneCount() {
        if (!this.viewer) return 0;
        return this.viewer.getSceneCount();
    }

    getSceneIndexByModelId(modelId) {
        if (!this.viewer) return -1;

        const loadedModel = this.loadedSplatModels.find(model => model.modelId === modelId);
        if (loadedModel && loadedModel.sceneIndex !== undefined) {
            return loadedModel.sceneIndex;
        }

        for (let i = 0; i < this.getSceneCount(); i++) {
            const scene = this.getScene(i);
            if (scene && scene.modelId === modelId) {
                return i;
            }
        }

        return -1;
    }

    getSplatMeshAndScene(modelId) {
        if (!this.viewer) return null;

        const underlyingViewer = this.viewer.viewer;
        const splatMesh = underlyingViewer.getSplatMesh();

        if (!splatMesh) return null;

        const sceneIndex = this.getSceneIndexByModelId(modelId);
        if (sceneIndex === -1) return null;

        const scene = splatMesh.scenes[sceneIndex];
        if (!scene) return null;

        return {splatMesh, scene, sceneIndex};
    }

    isGaussianSplatType(modelId) {
        const modelObject = getModelObject(modelId);
        if (modelObject) {
            if (modelObject.modeller === this.GaussianSplatType || modelObject.modelType === this.GaussianSplatType) {
                return true
            }
            if (modelObject.cacheHeader?.modeller === this.GaussianSplatType) {
                return true;
            }
        }
        return false;
    }

    setModelVisibility(modelId, visible) {
        const result = this.getSplatMeshAndScene(modelId);
        if (!result) return;

        const {splatMesh, sceneIndex} = result;
        const underlyingViewer = this.viewer.viewer;

        if (!underlyingViewer.enableOptionalEffects) return;

        if (splatMesh.material.uniforms.sceneVisibility &&
            splatMesh.material.uniforms.sceneVisibility.value) {

            splatMesh.material.uniforms.sceneVisibility.value[sceneIndex] = visible ? 1 : 0;
            splatMesh.material.uniformsNeedUpdate = true;

            const scene = this.getScene(sceneIndex);
            if (scene) {
                scene.visible = visible;
            }
            this.modelVisibility.set(modelId, visible);
        }
    }

    setModelOpacity(modelId, opacity) {
        const result = this.getSplatMeshAndScene(modelId);
        if (!result) return;

        const {splatMesh, sceneIndex} = result;
        const underlyingViewer = this.viewer.viewer;

        if (!underlyingViewer.enableOptionalEffects) return;

        if (splatMesh.material.uniforms.sceneOpacity &&
            splatMesh.material.uniforms.sceneOpacity.value) {

            splatMesh.material.uniforms.sceneOpacity.value[sceneIndex] = Math.max(0, Math.min(1, opacity));
            splatMesh.material.uniformsNeedUpdate = true;

            const scene = this.getScene(sceneIndex);
            if (scene) {
                scene.opacity = opacity;
            }
        }
    }

    setModelPosition(modelId, position) {
        const result = this.getSplatMeshAndScene(modelId);
        if (!result) return;

        const {splatMesh, scene} = result;

        if (Array.isArray(position)) {
            scene.position.set(position[0], position[1], position[2]);
        } else {
            scene.position.copy(position);
        }

        scene.updateTransform(splatMesh.dynamicMode);
        splatMesh.updateTransforms();
    }

    setModelRotation(modelId, rotation) {
        const result = this.getSplatMeshAndScene(modelId);
        if (!result) return;

        const {splatMesh, scene} = result;

        if (Array.isArray(rotation)) {
            scene.quaternion.set(rotation[0], rotation[1], rotation[2], rotation[3]);
        } else if (rotation.isQuaternion) {
            scene.quaternion.copy(rotation);
        } else if (rotation.isEuler) {
            scene.quaternion.setFromEuler(rotation);
        } else {
            return;
        }

        scene.updateTransform(splatMesh.dynamicMode);
        splatMesh.updateTransforms();
    }

    setModelScale(modelId, scale) {
        const result = this.getSplatMeshAndScene(modelId);
        if (!result) return;

        const {splatMesh, scene} = result;

        if (Array.isArray(scale)) {
            scene.scale.set(scale[0], scale[1], scale[2]);
        } else if (typeof scale === 'number') {
            scene.scale.set(scale, scale, scale);
        } else {
            scene.scale.copy(scale);
        }

        scene.updateTransform(splatMesh.dynamicMode);
        splatMesh.updateTransforms();
    }

    setModelTransform(modelId, transforms) {
        const result = this.getSplatMeshAndScene(modelId);
        if (!result) return;

        const {splatMesh, scene} = result;

        if (transforms.position) {
            if (Array.isArray(transforms.position)) {
                scene.position.set(transforms.position[0], transforms.position[1], transforms.position[2]);
            } else {
                scene.position.copy(transforms.position);
            }
        }

        if (transforms.rotation) {
            if (Array.isArray(transforms.rotation)) {
                scene.quaternion.set(transforms.rotation[0], transforms.rotation[1],
                    transforms.rotation[2], transforms.rotation[3]);
            } else if (transforms.rotation.isQuaternion) {
                scene.quaternion.copy(transforms.rotation);
            } else if (transforms.rotation.isEuler) {
                scene.quaternion.setFromEuler(transforms.rotation);
            }
        }

        if (transforms.scale) {
            if (Array.isArray(transforms.scale)) {
                scene.scale.set(transforms.scale[0], transforms.scale[1], transforms.scale[2]);
            } else if (typeof transforms.scale === 'number') {
                scene.scale.set(transforms.scale, transforms.scale, transforms.scale);
            } else {
                scene.scale.copy(transforms.scale);
            }
        }

        scene.updateTransform(splatMesh.dynamicMode);
        splatMesh.updateTransforms();
    }

    async removeModel(modelId) {
        if (!this.viewer) return Promise.resolve();

        const modelIndex = this.loadedSplatModels.findIndex(model => model.modelId === modelId);
        if (modelIndex === -1) return Promise.resolve();

        const sceneIndex = this.getSceneIndexByModelId(modelId);
        if (sceneIndex !== -1) {
            const removalPromise = this.viewer.removeSplatScenes([sceneIndex], false);
            modelManager.unregisterModel(modelId);
            this.loadedSplatModels.splice(modelIndex, 1);

            this.modelVisibility.delete(modelId);
            await removalPromise;

            this.syncModelStateWithViewer();
        }

        return Promise.resolve();
    }

    removeAllModels(showLoadingUI = false) {
        if (!this.viewer) return Promise.resolve();

        const sceneCount = this.viewer.getSceneCount();
        const sceneIndices = [...Array(sceneCount).keys()];
        const removalPromise = this.viewer.removeSplatScenes(sceneIndices, showLoadingUI);

        for (let splatModel of this.loadedSplatModels) {
            modelManager.unregisterModel(splatModel.modelId);
        }

        this.loadedSplatModels = [];
        this.modelVisibility.clear();
        if (this.viewer.parent) {
            this.viewer.parent.remove(this.viewer);
            this.viewer.parent = null;
        }

        return removalPromise;
    }

    syncModelStateWithViewer() {
        if (!this.modelVisibility || this.modelVisibility.size === 0) return;

        for (const [modelId, visible] of this.modelVisibility.entries()) {
            this.setModelVisibility(modelId, visible);
        }
    }

    setPointCloudMode(enabled) {
        if (!this.viewer) return;
        this.viewer.viewer.getSplatMesh().setPointCloudModeEnabled(enabled);
    }

    getViewer() {
        return this.viewer;
    }

    async dispose() {
        if (!this.viewer) return Promise.resolve();

        if (this.viewer.parent) {
            this.viewer.parent.remove(this.viewer);
        }

        this.removeAllModels();
        const disposePromise = this.viewer.dispose ? this.viewer.dispose() : Promise.resolve();

        this.loadedSplatModels = [];
        this.modelVisibility.clear();
        this.viewer = null;

        return disposePromise;
    }
}
